import React, { useEffect, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import Home from "./pages/Home";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ServicePage from "./pages/ServicePage";
import ServiceHub from "./pages/ServiceHub";
import LocalServicePage from "./pages/LocalServicePage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import InstantEstimate from "./pages/InstantEstimate";
import NoClimbFence from "./pages/NoClimbFence";
import RanchHorseFence from "./pages/RanchHorseFence";
import FinancingPage from "./pages/FinancingPage";
import SalesLogin from "./pages/sales/SalesLogin";
import SalesPortal from "./pages/sales/SalesPortal";
import OpportunityDetail from "./pages/sales/OpportunityDetail";
import CommercialAccountDetail from "./pages/sales/CommercialAccountDetail";
import BidDetail from "./pages/sales/BidDetail";
import AddLead from "./pages/sales/AddLead";
import QuoteSign from "./pages/QuoteSign";
import { trackPageView, trackVisitor } from "./utils/analytics";
import ChatWidget from "./components/ChatWidget";

function PageTracker() {
  const location = useLocation();
  const track = useCallback(() => {
    if (!location.pathname.startsWith('/admin') && !location.pathname.startsWith('/sales')) {
      trackPageView(location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => { track(); }, [track]);
  return null;
}

function App() {
  useEffect(() => { trackVisitor(); }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <PageTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Service Hub Pages */}
            <Route path="/pipe-fencing" element={<ServiceHub />} />
            <Route path="/land-services" element={<ServiceHub />} />
            <Route path="/metal-buildings" element={<ServiceHub />} />
            <Route path="/concrete" element={<ServiceHub />} />
            <Route path="/custom-gates" element={<ServiceHub />} />
            <Route path="/gravel-dirt-work" element={<ServiceHub />} />

            {/* Standalone Fence Pages */}
            <Route path="/no-climb-fence" element={<NoClimbFence />} />
            <Route path="/ranch-horse-fence" element={<RanchHorseFence />} />

            {/* Local Landing Pages */}
            <Route path="/pipe-fence-springtown-tx" element={<LocalServicePage />} />
            <Route path="/pipe-fence-weatherford-tx" element={<LocalServicePage />} />
            <Route path="/pipe-fence-azle-tx" element={<LocalServicePage />} />
            <Route path="/metal-buildings-springtown-tx" element={<LocalServicePage />} />
            <Route path="/metal-buildings-weatherford-tx" element={<LocalServicePage />} />
            <Route path="/metal-buildings-azle-tx" element={<LocalServicePage />} />
            <Route path="/land-clearing-springtown-tx" element={<LocalServicePage />} />
            <Route path="/land-clearing-weatherford-tx" element={<LocalServicePage />} />
            <Route path="/land-clearing-azle-tx" element={<LocalServicePage />} />
            <Route path="/concrete-contractor-springtown-tx" element={<LocalServicePage />} />
            <Route path="/concrete-contractor-weatherford-tx" element={<LocalServicePage />} />
            <Route path="/concrete-contractor-azle-tx" element={<LocalServicePage />} />
            <Route path="/gravel-driveways-springtown-tx" element={<LocalServicePage />} />
            <Route path="/dirt-work-springtown-tx" element={<LocalServicePage />} />
            <Route path="/custom-gates-springtown-tx" element={<LocalServicePage />} />

            {/* SEO 301 Redirects: consolidated legacy pages → authoritative hubs */}
            <Route path="/services/pipe-fence-installation" element={<Navigate to="/pipe-fencing" replace />} />
            <Route path="/services/ranch-fence" element={<Navigate to="/ranch-horse-fence" replace />} />
            <Route path="/services/custom-gates" element={<Navigate to="/custom-gates" replace />} />
            <Route path="/services/shops-buildings" element={<Navigate to="/metal-buildings" replace />} />

            {/* Legacy service pages (remaining distinct long-tail pages) */}
            <Route path="/services/:serviceSlug" element={<ServicePage />} />

            <Route path="/instant-estimate" element={<InstantEstimate />} />
            <Route path="/financing" element={<FinancingPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />

            {/* Sales CRM Portal */}
            <Route path="/sales/login" element={<SalesLogin />} />
            <Route path="/sales" element={<SalesPortal />} />
            <Route path="/sales/portal" element={<SalesPortal />} />
            <Route path="/sales/opportunity/:id" element={<OpportunityDetail />} />
            <Route path="/sales/commercial/account/:accountId" element={<CommercialAccountDetail />} />
            <Route path="/sales/bids/:bidId" element={<BidDetail />} />
            <Route path="/sales/leads/new" element={<AddLead />} />
            <Route path="/quote/sign/:token" element={<QuoteSign />} />
          </Routes>
          <ChatWidget />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </HelmetProvider>
  );
}

export default App;
